import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Snackbar } from "@mui/material";
import { useRecording } from "../../context/RecordingContext";
import blinkscribeapi from "../../api/blinkscribeapi";
import { eventBus } from "../../utils/eventBus";
import { isV2ForVisit, newerV2, v2StoreKey } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderV2";
import { labelText, openOrderReview, orderReviewContext, orderReviewLabel, registerOrderReviewVisit, setOrderReviewLabel } from "./OrderReviewHost";
import { buildSections, emptyRecord, leftoverCount, readRecord, reviewEnabled, reviewStoreKey, visitKeyOf, writeRecord } from "./orderReviewModel";
import { FALLBACK_ORDER_ALIASES, isAddedOrder } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderKey";
import { cardMounted, liveVisit, reviewContextFor, visitOrdersOf } from "./liveOrdersVisit";

/* The end-of-visit order review, whichever tab is open (hubfix-live-orders, owner 10-23).

   Live 10-22/23: the review did not open on 28 visits that had a final pass, because only the CareIQ tab opened it
   (the provider was on another tab when the recording stopped). Mounted once in App.js (inside RecordingProvider):
   when a recording whose session has the live Orders card or the review (socket live_orders / order_suggestions_v2
   with review.enabled) stops, this reads BlinkScribe's final payload (GET order-suggestions/visit, a few tries),
   stores it where the review and the cards read it, makes sure the review can place (the CareIQ tab's context, else
   the live card's visit context: the same orders/create path), then
     - this visit's chart is open (its live card is mounted): opens the review (it shows itself only when rows are
       left)
     - otherwise (another chart, or no card ever registered the visit): ONLY a toast "2 suggested orders · Maria
       Delgado · 10:30 AM · Review" - the review is never opened by itself over another patient's chart (review
       10-23). The toast and the review's title name the patient + visit time (the card's chart row, else the
       recording's patient).
   Once per visit (the review record's autoShown, shared with the CareIQ tab's own trigger). Ids only. */

let DELAYS = [1500, 4000, 8000, 15000, 30000];
// tests only: the GET retry delays
export const setLiveHostDelays = (d) => { DELAYS = Array.isArray(d) && d.length ? d : [1500, 4000, 8000, 15000, 30000]; };

const readStored = (pid, appt) => {
  try {
    const raw = localStorage.getItem(v2StoreKey(pid, appt));
    const st = raw ? JSON.parse(raw) : null;
    return st && st.payload ? st.payload : null;
  } catch (_) {
    return null;
  }
};
const store = (pid, appt, p) => {
  const prev = readStored(pid, appt);
  const payload = newerV2(prev, p);
  try { localStorage.setItem(v2StoreKey(pid, appt), JSON.stringify({ enabled: true, payload })); } catch (_) { /* non-fatal */ }
  eventBus.emit("liveOrdersStored", { patientId: pid, apptKey: appt });
  return payload;
};
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

const activeOf = (lo, v2) => {
  if (lo && lo.enabled === true && lo.patientid && lo.appointmentid) return { pid: String(lo.patientid), appt: String(lo.appointmentid) };
  if (v2 && v2.review && v2.review.enabled === true && v2.patientid && v2.appointmentid) return { pid: String(v2.patientid), appt: String(v2.appointmentid) };
  return null;
};

const LiveOrdersHost = () => {
  const { liveOrders, orderSuggestionsV2, isGlobalRecording, patientInfo } = useRecording();
  const activeRef = useRef(null);
  const wasRec = useRef(false);
  const [toast, setToast] = useState(null);         // { pid, appt, n, label }

  useEffect(() => {
    const a = activeOf(liveOrders, orderSuggestionsV2);
    if (!a) return;
    // the recording's patient name (patientInfo is cleared at the stop: kept here while it matches the visit)
    const prev = activeRef.current;
    const pi = patientInfo && String(patientInfo.patientid ?? "") === a.pid ? patientInfo : null;
    const name = pi ? [pi.firstname, pi.lastname].filter((x) => x && String(x).trim()).join(" ").trim() : "";
    const keep = prev && prev.pid === a.pid && prev.appt === a.appt ? prev.name : "";
    activeRef.current = { ...a, name: name || keep || "" };
  }, [liveOrders, orderSuggestionsV2, patientInfo]);

  const endOfVisit = useCallback(async ({ pid, appt, name = "" }) => {
    const visitKey = visitKeyOf(pid, appt);
    let payload = null;
    for (const ms of DELAYS) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(ms);
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await blinkscribeapi.get("/v1/recording/order-suggestions/visit", {
          params: { appointmentid: Number(appt), patientid: Number(pid) }, timeout: 15000,
        });
        const p = res && res.payload;
        if (isV2ForVisit(p, pid, appt)) payload = store(pid, appt, p);
        if ((p && p.final) || (res && res.enabled === false)) break;
      } catch (_) {
        // keep trying; the last stored payload is used
      }
    }
    payload = payload || readStored(pid, appt);
    if (!reviewEnabled(payload)) return;
    const key = reviewStoreKey(pid, appt);
    const rec = readRecord(key) || emptyRecord();
    if (rec.state === "confirmed" || rec.autoShown) return;
    const info = liveVisit(visitKey);
    const own = orderReviewContext(visitKey);
    if (!own && info) registerOrderReviewVisit(visitKey, { current: reviewContextFor(info, payload) });
    else if (own && !own.payload) own.payload = payload;
    const known = orderReviewLabel(visitKey);
    const label = (info && info.label) || known || (name ? { name, time: "" } : null);
    if (label) setOrderReviewLabel(visitKey, label);
    writeRecord(key, { ...rec, autoShown: true });
    if (info && cardMounted(visitKey) > 0) {
      openOrderReview({ patientId: pid, apptKey: appt, trigger: "stop", label });
      return;
    }
    // this visit's chart is not open (or no card registered it): count what is left and offer it in a toast only
    const orders = (info ? await visitOrdersOf(info) : null) || [];
    const sections = buildSections(payload, {
      rec: readRecord(key) || emptyRecord(), aliases: FALLBACK_ORDER_ALIASES, visitOrders: orders,
      isAdded: (k) => isAddedOrder(k, { aliases: FALLBACK_ORDER_ALIASES, currentOrders: orders }),
    });
    const n = leftoverCount(sections);
    if (n > 0) setToast({ pid, appt, n, label });
    else if (info) openOrderReview({ patientId: pid, apptKey: appt, trigger: "stop", label });   // review_skipped, never shows
  }, []);

  useEffect(() => {
    const was = wasRec.current;
    wasRec.current = Boolean(isGlobalRecording);
    if (!was || isGlobalRecording || !activeRef.current) return;
    const visit = activeRef.current;
    activeRef.current = null;
    endOfVisit(visit).catch(() => {});
  }, [isGlobalRecording, endOfVisit]);

  if (!toast) return null;
  const close = () => setToast(null);
  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      onClose={(_e, reason) => { if (reason !== "clickaway") close(); }}
      message={`${toast.n} suggested order${toast.n === 1 ? "" : "s"} from the last visit${labelText(toast.label) ? ` · ${labelText(toast.label)}` : ""}`}
      data-testid="live-orders-toast"
      action={(
        <>
          <Button
            size="small"
            color="primary"
            onClick={() => { const t = toast; close(); openOrderReview({ patientId: t.pid, apptKey: t.appt, trigger: "toast", label: t.label }); }}
          >
            Review
          </Button>
          <Button size="small" color="inherit" onClick={close}>Later</Button>
        </>
      )}
    />
  );
};

export default LiveOrdersHost;
